import React, { useMemo } from "react";
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid-pro";
import { Box, Paper, Typography } from "@mui/material";
import CommentsAndFiles from "../../../../components/Discussion/CommentsAndFiles";
import { formatDateToDDMMYY } from "../../../../utils/helpers";

interface LogDataItem {
  [key: string]: {
    type: string;
    order?: string;
    title?: string;
    value: any;
    description?: string;
    format?: string;
  };
}

interface TaskData {
  data: {
    log_data: LogDataItem[];
  };
  reference_id?: string;
  shared_uid: string;
}

interface DynamicDataTableProps {
  filteredTasksByTaskStructureSharedUid: TaskData[];
}

const OccurrencesTable: React.FC<DynamicDataTableProps> = ({
  filteredTasksByTaskStructureSharedUid,
}) => {
  const [paginationModel, setPaginationModel] =
    React.useState<GridPaginationModel>({
      page: 0,
      pageSize: 10,
    });

  const shared_uid = filteredTasksByTaskStructureSharedUid?.[0]?.shared_uid;

  // Extract dynamic columns from log_data
  const columns: GridColDef[] = useMemo(() => {
    if (!filteredTasksByTaskStructureSharedUid?.length) return [];

    const columnMap = new Map<string, any>();

    // Collect all unique column keys and their metadata
    filteredTasksByTaskStructureSharedUid.forEach((task) => {
      if (task?.data?.log_data) {
        task.data.log_data.forEach((log) => {
          Object.entries(log).forEach(([key, metadata]) => {
            if (!columnMap.has(key)) {
              columnMap.set(key, {
                field: key,
                headerName: metadata.title || key,
                order: parseInt(metadata.order || "999"),
                type: metadata.type,
                format: metadata.format,
                width: 150,
                sortable: true,
              });
            }
          });
        });
      }
    });

    // Convert map to array and sort by order
    const cols = Array.from(columnMap.values()).sort((a, b) => {
      // Always place Execution Time first
      if (a.field === "meta_data:start_run") return -1;
      if (b.field === "meta_data:start_run") return 1;

      return a.order - b.order;
    });

    // Format columns based on type
    return cols.map((col) => {
      const formattedCol: GridColDef = {
        field: col.field,
        headerName: col.headerName,
        width: col.width,
        sortable: col.sortable,
      };

      // Handle date/datetime formatting
      if (col.format === "date" || col.format === "date-time") {
        formattedCol.type = "string";
        formattedCol.valueFormatter = (value) => {
          return formatDateToDDMMYY(value);
        };
        formattedCol.width = 110;
      }
      // Handle number type
      else if (col.type === "number") {
        formattedCol.type = "number";
        formattedCol.width = 130;
        formattedCol.align = "left";
        formattedCol.headerAlign = "left";
      }
      // Handle string type
      else {
        formattedCol.type = "string";
      }

      return formattedCol;
    });
  }, [filteredTasksByTaskStructureSharedUid]);

  // Flatten data into rows
  const rows = useMemo(() => {
    if (!filteredTasksByTaskStructureSharedUid?.length) return [];

    const flatRows: any[] = [];
    let rowId = 0;

    filteredTasksByTaskStructureSharedUid.forEach((task) => {
      if (task?.data?.log_data) {
        task.data.log_data.forEach((log) => {
          const row: any = { id: rowId++ };

          // Extract value from each field
          Object.entries(log).forEach(([key, metadata]) => {
            row[key] = metadata.value;
          });

          flatRows.push(row);
        });
      }
    });

    return flatRows;
  }, [filteredTasksByTaskStructureSharedUid]);

  if (!filteredTasksByTaskStructureSharedUid?.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
        No data available
      </Box>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mt: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          overflow: "hidden", // ensures seamless top-to-bottom visual
        }}
      >
        {/* Custom Header Section */}
        <Box
          sx={{
            backgroundColor: "#fafafa", // same as DataGrid header background
            borderBottom: "1px solid #e0e0e0",
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="h6" component="h2">
            Occurrences
          </Typography>
        </Box>

        {/* DataGrid Section */}

        <DataGridPro
          rows={rows}
          columns={columns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnFilter
          disableColumnReorder
          sortingOrder={["asc", "desc"]}
          isCellEditable={() => false}
          density="compact"
          sx={{
            border: "none", // hide inner borders since Paper already has them
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none",
              },
            "& .MuiDataGrid-cellEmpty": {
              width: "auto",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiDataGrid-row": {
              "&:hover": { backgroundColor: "#f9f9f9" },
              "&.Mui-selected": {
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "#f9f9f9" },
              },
            },
          }}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } },
          }}
        />
      </Paper>
      {shared_uid && (
        <CommentsAndFiles
          key={`comments-files-${shared_uid}`}
          objectType="tasks"
          referenceModule="Data Accuracy"
          moduleType="dataAccuracy"
          objectSharedUid={shared_uid}
          postCommentTrackID="178"
          reactionTrackID="469"
          uploadFileTrackID="175"
          downloadFileTrackID="471"
          previewFileTrackID="523"
        />
      )}
    </>
  );
};

export default OccurrencesTable;
